import React, { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  AddDocumentIcon,
  CircleConfirmIcon,
  CloseCircleIcon,
  EditIcon,
  ThreeDotsIcon,
  TrashBinIcon,
} from '../../../assets/icons/index';
import { AlertDialogWrapper } from '../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../components/hover-card/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/popover/index';
import { Tooltip } from '../../../components/tooltip/index';
import { useFormat, useModalState } from '../../../hooks/index';
import { ShareClass } from '../../../types/share-classes.types';
import { getS3FileOriginalName } from '../../../utils/get-s3file-original-name.util';
import { FilesModal } from '../../events/modals/index';

export type ShareClassItemProps = {
  handleDelete: (id: string) => void;
  handleDocumentClick: (id: string) => void;
  handleEdit: (id: string) => void;
} & Pick<
  ShareClass,
  | 'antiDilutionRights'
  | 'conversionRatio'
  | 'conversionRatioEnabled'
  | 'createdAt'
  | 'creationDate'
  | 'dividendRightEnabled'
  | 'docusignFileLinks'
  | 'filesLinks'
  | 'id'
  | 'isDefault'
  | 'isUsed'
  | 'liquidityPreferences'
  | 'name'
  | 'stakeholders'
  | 'votingRight'
  | 'votingRightEnabled'
>;

export const ShareClassTableItem: FC<ShareClassItemProps> = ({
  id,
  handleDelete,
  handleEdit,
  handleDocumentClick,
  name,
  conversionRatio,
  dividendRightEnabled,
  votingRight,
  votingRightEnabled,
  antiDilutionRights,
  creationDate,
  stakeholders,
  docusignFileLinks = [],
  filesLinks,
  liquidityPreferences,
  isDefault,
  isUsed,
  conversionRatioEnabled,
  createdAt,
}) => {
  const { format } = useFormat();

  const handleDownloadAllDocuments = async (links: string[]) => {
    for (let i = 0; i < links.length; i++) {
      await new Promise<void>((resolve) => {
        const link = links[i];
        handleDocumentClick(link);

        setTimeout(() => {
          resolve();
        }, 1000);
      });
    }
  };

  const {
    isOpen: isOpenFilesModal,
    handleSuccessModal: handleSuccessFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState({ onSuccess: () => handleDownloadAllDocuments(filesLinks) });
  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={new Date(createdAt)}
          docusignFileLinks={docusignFileLinks}
          filesLinks={filesLinks}
          name={name}
          onClose={handleCloseFilesModal}
          onDownloadFile={handleDocumentClick}
          onSuccess={handleSuccessFilesModal}
        />
      </AlertDialogWrapper>
      <tr className="w-full bg-white" key={id}>
        <td className="max-w-[300px] truncate p-4 text-sm text-gray-700">
          <Tooltip content={name}>{name}</Tooltip>
        </td>
        <td className="p-4 text-sm text-gray-700">{format(creationDate || '', 'dd/MM/yyy')}</td>
        <td className="p-4 text-sm text-gray-700">{stakeholders}</td>
        <td className="p-4">
          <div className="flex flex-col gap-2 text-xs text-gray-700">
            <div className="flex items-center gap-1">
              {dividendRightEnabled ? (
                <CircleConfirmIcon />
              ) : (
                <CloseCircleIcon className="size-4" />
              )}
              <AppFormattedMessage id="DIVIDENDS" />
            </div>

            <div className="flex items-center gap-1">
              {antiDilutionRights ? <CircleConfirmIcon /> : <CloseCircleIcon className="size-4" />}
              <AppFormattedMessage id="ANTI_DILUTION" />
            </div>
          </div>
        </td>
        <td className="p-4 text-sm text-gray-700">{votingRightEnabled ? votingRight : '-'}</td>
        <td className="p-4 text-sm text-gray-700">
          {conversionRatioEnabled ? conversionRatio.toFixed(2) + 'x' : '-'}
        </td>
        <td className="p-4 text-sm text-gray-700">
          {liquidityPreferences ? liquidityPreferences?.seniority : '-'}
        </td>

        <td className="p-4 text-xs text-gray-700">
          {liquidityPreferences ? (
            <div className="flex flex-col text-wrap">
              <span>
                <AppFormattedMessage id="MULTIPLE" />: {liquidityPreferences.multiple}
              </span>
              {liquidityPreferences.participating?.capValue && (
                <span>
                  <AppFormattedMessage id="PARTICIPATING_CAP" />:{' '}
                  {liquidityPreferences.participating?.capValue}
                </span>
              )}
              {liquidityPreferences.participating?.interest && (
                <span>
                  <AppFormattedMessage id="INTEREST" />:{' '}
                  {liquidityPreferences.participating?.interest}%
                </span>
              )}
            </div>
          ) : (
            '-'
          )}
        </td>
        <td className="pl-4 text-sm text-gray-700">
          {filesLinks?.length > 0 ? (
            <HoverCard>
              <HoverCardTrigger>
                <AddDocumentIcon
                  className="size-6 cursor-pointer p-[2px]"
                  onClick={() =>
                    filesLinks.length > 0 &&
                    (filesLinks.length === 1
                      ? handleDocumentClick(filesLinks[0])
                      : handleOpenFilesModal())
                  }
                />
              </HoverCardTrigger>
              <HoverCardContent
                className="max-w-[360px] rounded-lg bg-[#101828] px-3 py-2"
                side="top"
              >
                <span className="block w-full truncate p-0 text-label-md font-[550] text-white">
                  {filesLinks.length === 1
                    ? getS3FileOriginalName(filesLinks[0])
                    : `${filesLinks.length} documents attached`}
                </span>
              </HoverCardContent>
            </HoverCard>
          ) : (
            '-'
          )}
        </td>

        <td className="p-4 text-label-md font-[450]">
          <div className="flex h-full w-full shrink-0 items-center justify-between gap-4 pr-4">
            {isUsed ? (
              <span className="rounded-2xl bg-forest-50 px-2 py-[2px] text-forest-600">
                <AppFormattedMessage id="FILTER_IN_USE" />
              </span>
            ) : (
              <span className="rounded-2xl bg-fireside-50 px-2 py-[2px] text-fireside-600">
                <AppFormattedMessage id="FILTER_NOT_USED" />
              </span>
            )}
            <Popover>
              <PopoverTrigger
                className="h-fit w-fit rounded data-[state=open]:shadow-md"
                data-permission-required
                disabled={isDefault}
              >
                <div
                  className={twMerge(
                    'flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors',
                    !isDefault && 'hover:bg-gray-100',
                  )}
                >
                  <ThreeDotsIcon iconColor={isDefault ? '#D0D5DD' : '#344054'} />
                </div>
              </PopoverTrigger>
              <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
                <PopoverClose>
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
                    data-permission-required
                    onClick={() => handleEdit(id)}
                  >
                    <EditIcon className="size-4" />
                    <span className="text-sm font-normal text-gray-700">
                      <AppFormattedMessage id="EDIT" />
                    </span>
                  </div>
                  <div
                    className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-fireside-500 hover:bg-gray-50"
                    data-permission-required
                    onClick={() => handleDelete(id)}
                  >
                    <TrashBinIcon className="size-4" iconColor="#F04438" />
                    <span className="text-sm font-normal text-fireside-500">
                      <AppFormattedMessage id="DELETE" />
                    </span>
                  </div>
                </PopoverClose>
              </PopoverContent>
            </Popover>
          </div>
        </td>
      </tr>
    </>
  );
};
